/**
 * Motion-based auto start / auto stop detection for flight recording.
 *
 * Pure state machine: callers feed GPS-derived motion samples and act on the
 * returned decision, so the policy is testable without GPS, timers or Vue.
 *
 * Auto start (fed from the idle GPS watch, before recording):
 *  - `taxi`    — sustained ground speed above the taxi threshold.
 *  - `takeoff` — sustained ground speed above the takeoff threshold *and* a
 *                sustained climb, i.e. forward movement plus elevation gain.
 *
 * Auto stop (fed from the recorder's own track points, so it also works while
 * the app is backgrounded): sustained near-zero ground speed once the flight
 * has actually moved — which covers both a landing rollout and the end of a
 * taxi back to the apron.
 */

export type AutoStartMode = 'off' | 'taxi' | 'takeoff';

/** Why recording stopped itself — used for the message shown to the pilot. */
export type AutoStopReason = 'landed' | 'taxi-ended';

export type MotionSample = {
  timestampMs: number;
  /** Ground speed in km/h, or null when the fix carries no usable speed. */
  speedKmh: number | null;
  /** Altitude in metres, or null when the fix carries no altitude. */
  altitudeM: number | null;
  /** Horizontal accuracy in metres, or null when unknown. */
  accuracyM: number | null;
};

export type AutoRecordConfig = {
  startMode: AutoStartMode;
  autoStop: boolean;
  /** Ground speed that counts as taxiing (km/h). */
  taxiSpeedKmh: number;
  /** Ground speed that counts as a takeoff roll (km/h). */
  takeoffSpeedKmh: number;
  /** Climb rate that counts as leaving the ground (m/s). */
  climbRateMps: number;
  /** Trailing window the climb rate is measured over (ms). */
  climbWindowMs: number;
  /** The start condition must hold continuously for this long (ms). */
  startDwellMs: number;
  /** At or below this ground speed the aircraft counts as stopped (km/h). */
  stopSpeedKmh: number;
  /** The stop condition must hold continuously for this long (ms). */
  stopDwellMs: number;
  /** Never auto stop within this long of the recording starting (ms). */
  minRecordingMs: number;
  /** Ignore fixes worse than this (m); they produce phantom speed spikes. */
  maxAccuracyM: number;
  /** Altitude gain during the recording above which a stop reads as a landing (m). */
  airborneGainM: number;
  /** After a recording ends, ignore start conditions for this long (ms). */
  restartCooldownMs: number;
  /** A gap longer than this between fixes restarts any dwell in progress (ms). */
  maxSampleGapMs: number;
};

/** Thresholds tuned for light aircraft / gliders; the pilot only picks mode + on/off. */
export const AUTO_RECORD_THRESHOLDS: Omit<AutoRecordConfig, 'startMode' | 'autoStop'> = {
  taxiSpeedKmh: 15,
  takeoffSpeedKmh: 45,
  climbRateMps: 1.5,
  climbWindowMs: 20_000,
  startDwellMs: 10_000,
  stopSpeedKmh: 5,
  stopDwellMs: 120_000,
  minRecordingMs: 60_000,
  maxAccuracyM: 50,
  airborneGainM: 60,
  restartCooldownMs: 300_000,
  maxSampleGapMs: 30_000,
};

export type AutoRecordDetector = {
  /** Feed a fix taken while idle. Returns 'start' when recording should begin. */
  observeIdle: (sample: MotionSample) => 'start' | null;
  /** Feed a recorded track point. Returns a reason when recording should end. */
  observeRecording: (sample: MotionSample) => AutoStopReason | null;
  /** Recording began — arms auto stop and clears any pending start. */
  beginRecording: (atMs: number) => void;
  /** Recording ended — clears state and suppresses auto start for the cooldown. */
  endRecording: (atMs: number) => void;
  /** True while the start condition is holding but has not yet met its dwell. */
  isStartPending: () => boolean;
  reset: () => void;
};

function isUsable(sample: MotionSample, maxAccuracyM: number): boolean {
  if (!Number.isFinite(sample.timestampMs)) return false;
  if (sample.accuracyM != null && Number.isFinite(sample.accuracyM) && sample.accuracyM > maxAccuracyM) {
    return false;
  }
  return true;
}

function pruneWindow(samples: MotionSample[], nowMs: number, windowMs: number): void {
  const cutoff = nowMs - windowMs;
  // Keep one sample at or before the cutoff so the window really spans `windowMs`.
  while (samples.length >= 2 && samples[1].timestampMs <= cutoff) {
    samples.shift();
  }
}

function hasAltitudeData(samples: MotionSample[]): boolean {
  return samples.some((s) => s.altitudeM != null && Number.isFinite(s.altitudeM));
}

/**
 * Climb rate in m/s across the altitude-bearing samples in the window, or null
 * when they do not yet span `minSpanMs` (so a verdict would be noise).
 */
export function climbRateMpsOver(samples: MotionSample[], minSpanMs: number): number | null {
  const withAltitude = samples.filter((s) => s.altitudeM != null && Number.isFinite(s.altitudeM));
  if (withAltitude.length < 2) return null;

  const first = withAltitude[0];
  const last = withAltitude[withAltitude.length - 1];
  const spanMs = last.timestampMs - first.timestampMs;
  if (spanMs < minSpanMs || spanMs <= 0) return null;

  return ((last.altitudeM as number) - (first.altitudeM as number)) / (spanMs / 1000);
}

export function createAutoRecordDetector(getConfig: () => AutoRecordConfig): AutoRecordDetector {
  let idleWindow: MotionSample[] = [];
  let startCandidateSinceMs: number | null = null;
  let lastIdleSampleAtMs: number | null = null;
  let suppressStartUntilMs = 0;

  let recordingStartedAtMs: number | null = null;
  let hasMoved = false;
  let baseAltitudeM: number | null = null;
  let peakAltitudeGainM = 0;
  let slowSinceMs: number | null = null;
  let lastRecordingSampleAtMs: number | null = null;
  let stopEmitted = false;

  function clearIdleState(): void {
    idleWindow = [];
    startCandidateSinceMs = null;
    lastIdleSampleAtMs = null;
  }

  function clearRecordingState(): void {
    recordingStartedAtMs = null;
    hasMoved = false;
    baseAltitudeM = null;
    peakAltitudeGainM = 0;
    slowSinceMs = null;
    lastRecordingSampleAtMs = null;
    stopEmitted = false;
  }

  function meetsStartCondition(config: AutoRecordConfig, sample: MotionSample): boolean {
    const speedKmh = sample.speedKmh;
    if (speedKmh == null || !Number.isFinite(speedKmh)) return false;

    if (config.startMode === 'taxi') {
      return speedKmh >= config.taxiSpeedKmh;
    }

    if (speedKmh < config.takeoffSpeedKmh) return false;

    const climbRate = climbRateMpsOver(idleWindow, config.climbWindowMs);
    if (climbRate != null) return climbRate >= config.climbRateMps;

    // No altitude at all from this device: fall back to the speed threshold once
    // the window is full, rather than never detecting a takeoff.
    if (hasAltitudeData(idleWindow)) return false;
    const spanMs = idleWindow.length >= 2
      ? idleWindow[idleWindow.length - 1].timestampMs - idleWindow[0].timestampMs
      : 0;
    return spanMs >= config.climbWindowMs;
  }

  return {
    observeIdle(sample: MotionSample): 'start' | null {
      const config = getConfig();
      if (config.startMode === 'off') {
        clearIdleState();
        return null;
      }
      if (!isUsable(sample, config.maxAccuracyM)) return null;

      // A long gap (GPS dropout, app suspended) invalidates any dwell in progress.
      if (
        lastIdleSampleAtMs != null &&
        sample.timestampMs - lastIdleSampleAtMs > config.maxSampleGapMs
      ) {
        idleWindow = [];
        startCandidateSinceMs = null;
      }
      lastIdleSampleAtMs = sample.timestampMs;

      idleWindow.push(sample);
      pruneWindow(idleWindow, sample.timestampMs, config.climbWindowMs);

      if (sample.timestampMs < suppressStartUntilMs) {
        startCandidateSinceMs = null;
        return null;
      }

      if (!meetsStartCondition(config, sample)) {
        startCandidateSinceMs = null;
        return null;
      }

      if (startCandidateSinceMs == null) {
        startCandidateSinceMs = sample.timestampMs;
      }
      if (sample.timestampMs - startCandidateSinceMs < config.startDwellMs) return null;

      clearIdleState();
      return 'start';
    },

    observeRecording(sample: MotionSample): AutoStopReason | null {
      const config = getConfig();
      if (!config.autoStop || stopEmitted) return null;
      if (!isUsable(sample, config.maxAccuracyM)) return null;

      if (sample.altitudeM != null && Number.isFinite(sample.altitudeM)) {
        if (baseAltitudeM == null) {
          baseAltitudeM = sample.altitudeM;
        } else {
          peakAltitudeGainM = Math.max(peakAltitudeGainM, sample.altitudeM - baseAltitudeM);
        }
      }

      if (
        lastRecordingSampleAtMs != null &&
        sample.timestampMs - lastRecordingSampleAtMs > config.maxSampleGapMs
      ) {
        slowSinceMs = null;
      }
      lastRecordingSampleAtMs = sample.timestampMs;

      const speedKmh = sample.speedKmh;
      if (speedKmh == null || !Number.isFinite(speedKmh)) return null;

      if (speedKmh >= config.taxiSpeedKmh) {
        hasMoved = true;
      }
      if (speedKmh > config.stopSpeedKmh) {
        slowSinceMs = null;
        return null;
      }

      // Never stop a recording that has not moved yet — the pilot may still be
      // sitting on the apron waiting for a clearance.
      if (!hasMoved) return null;

      if (slowSinceMs == null) {
        slowSinceMs = sample.timestampMs;
      }
      if (sample.timestampMs - slowSinceMs < config.stopDwellMs) return null;
      if (
        recordingStartedAtMs != null &&
        sample.timestampMs - recordingStartedAtMs < config.minRecordingMs
      ) {
        return null;
      }

      stopEmitted = true;
      return peakAltitudeGainM >= config.airborneGainM ? 'landed' : 'taxi-ended';
    },

    beginRecording(atMs: number): void {
      clearIdleState();
      clearRecordingState();
      recordingStartedAtMs = atMs;
    },

    endRecording(atMs: number): void {
      clearIdleState();
      clearRecordingState();
      suppressStartUntilMs = atMs + getConfig().restartCooldownMs;
    },

    isStartPending(): boolean {
      return startCandidateSinceMs != null;
    },

    reset(): void {
      clearIdleState();
      clearRecordingState();
      suppressStartUntilMs = 0;
    },
  };
}
